// ════════════════════════════════════════════════════════════
//  online/partnerBond.js — PARTNERS ARE FRIENDS, AUTOMATICALLY
// ════════════════════════════════════════════════════════════
//  caP'n 2026-10-18: "Partners should be able to find each other very easily —
//  they should be automatic friends etc so they or I can click on each other's
//  Mark and easily join whatever room they're in".
//
//  THE BOND = two Marks that name EACH OTHER as Partner (the Mark shows ⇄).
//  One-sided is never enough: anyone can type your code into their own
//  Partner field, and that must never make them your friend.
//
//  What it does, once per sign-in (and once more a minute later if a write
//  was refused): it reads my Mark's partner P and P's Mark's partner.
//  If they point at each other:
//    • no friendship edge yet → send the request (my edge "out", theirs "in")
//    • they already asked me ("in") → accept it (both "accepted")
//    • "out" (waiting on them) / "accepted" / "blocked" → nothing.
//      A BLOCK IS NEVER OVERRIDDEN.
//  Both partners' screens run this, so whoever opens Shatterproof first sends
//  and the other one's screen accepts. The writes are the exact shapes
//  ieSocial's own addByUid / accept use (rules.json's friends branch allows
//  them). Friends are Empire-wide (profiles/{uid}/friends), so the bond shows in
//  every IE game's friends list without touching any game's code or the Mark kit.
// ════════════════════════════════════════════════════════════
import { useEffect } from "react";
import { ref, get } from "firebase/database";
import { db } from "../firebase";
import { friendEdgePath } from "../ieSocial";
import { sendRequest, acceptRequest } from "./friendship.js";
import { bondStep, UID } from "./bondRule.js";
export { bondStep };

async function bondOnce(uid, myName) {
  const val = async (p) => { try { return (await get(ref(db, p))).val(); } catch (e) { return null; } };
  const p = await val(`profiles/${uid}/mark/partner`);
  if (!p || !UID.test(String(p)) || p === uid) return { done: true, why: "no partner" };
  const [theirs, edge, theirName] = await Promise.all([val(`profiles/${p}/mark/partner`), val(friendEdgePath(uid, p)), val(`profiles/${p}/name`)]);
  const step = bondStep({ me: uid, myPartner: p, theirPartner: theirs, edge });
  if (!step) return { done: true, why: theirs !== uid ? "not mutual" : `edge ${edge && edge.status}` };
  const them = theirName || (edge && edge.name);
  const r = step === "send" ? await sendRequest(uid, myName, p, them) : await acceptRequest(uid, myName, p, them);
  return r.ok ? { done: true, did: step } : { done: false, did: step, why: r.error };
}

/** Mount once in App. Runs when the account is signed in and writable. */
export function usePartnerBond(on) {
  const uid = on && on.writable && on.phase === "ready" ? on.authUid : null;
  const name = on && on.name;
  useEffect(() => {
    if (!uid) return undefined;
    let dead = false, timer = null;
    const go = async (tries) => {
      const r = await bondOnce(uid, name);
      if (dead) return;
      if (typeof window !== "undefined") window.__partnerBond = r;   // console: what the bond did this sign-in
      if (!r.done && tries > 0) timer = setTimeout(() => go(tries - 1), 60000);
    };
    go(1);
    return () => { dead = true; clearTimeout(timer); };
  }, [uid, name]);
}
